import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {Selector} from 'notebook/ELNConstants';
import {
  CreateMixtureGroupExtras,
  MixtureGroupMetadata,
} from 'notebook/integrations/registry/MixtureGroupConstants';
import * as GroupActionFunctionDAO from 'notebook/notes/groups/action-functions/GroupActionFunctionDAO';
import makeSimpleGroupActionFunction from 'notebook/notes/groups/action-functions/makeSimpleGroupActionFunction';
import {GroupActionFunctionParameters} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {isStructuredTableNotePart} from 'notebook/notes/groups/util/assertIsStructuredTableNotePart';
import {ACTION_REFRESH} from 'notebook/store/VersioningConstants';
import {
  ActionResultType,
  performClientSideWriteAction,
  performServerSideWriteAction,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  LastSyncedUpstreamValue,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import updateTableWithColumnUpdatesAndCells from 'notebook/structured-tables/util/updateTableWithColumnUpdatesAndCells';
import {RESYNC_TABLE_LABEL, TableData} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell, OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {
  handleRefreshEntitiesInStructuredTable,
  handleRefreshStructuredTable,
} from 'notebook/tables/refresh-table-data/RefreshTableData';
import {hasNLO, isStructuredTable} from 'notebook/TypePredicates';
import {AnyStructuredTable, NotePart, NotePartWithItem} from 'notebook/util/NoteUtilConstants';

/**
 * Refreshes and then returns a contained note part within a noteLinkedGroup.
 *
 * Performance note: Generic is constrained to `AnyStructuredTable` instead of the broader
 * `NotePartItem` type to avoid TypeScript computing all 1,287 members of that union, which
 * would take ~11 seconds to type-check. The function only handles structured tables anyway
 * (validates at runtime and returns early for other types).
 */
export function refreshContainedNotePart<T extends AnyStructuredTable>({
  containedNotePart,
  evaluatedTableMap,
  getIdFromCidFn,
  entryOrTemplateId,
  getItemWithUpdatedContent,
}: {
  containedNotePart: NotePartWithItem<T>;
  evaluatedTableMap: OutputTableMap;
  getIdFromCidFn: (id: string) => string | number | undefined;
  entryOrTemplateId: string;
  getItemWithUpdatedContent: (oldContainedNotePart: NotePart, content: object) => NotePart;
}): Promise<NotePartWithItem<T>> {
  // The `handleRefreshStructuredTable` just calls our callback and doesn't return any value.
  // This is usually fine as we would just call the updateGroup callback inside of our callback
  // function.  But in this case, we want to refresh each note part serially, calling our
  // GroupAdapter.reduceGroup after each one to make sure that our group is consistent before
  // refreshing the next item in the group.  So we wrap the entire operation in a promise which
  // is then resolved as part of the callback function, giving us access to the actual content as
  // the return of this function.
  // eslint-disable-next-line no-async-promise-executor -- Disabling for now, but worth fixing
  return new Promise((resolve, reject) => {
    assert(
      hasNLO(containedNotePart) && isStructuredTable(containedNotePart.item),
      'Trying to refresh an unexpected contained note part',
      {itemType: containedNotePart.type}
    );
    const structuredTable = containedNotePart.item;
    const structuredTableAdapter: UnknownStructuredTableAdapter =
      getStructuredTableAdapters()[structuredTable.kind];
    const table = structuredTableAdapter.toTable(structuredTable);
    const computedTable = evaluatedTableMap[table.name];
    const onUpdateTableCallback = (
      index: number,
      updatedContent: {
        table: TableData;
        lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
      },
      selectors?: Array<Selector>
    ): void => {
      const updatedContainedNotePart = getItemWithUpdatedContent(
        containedNotePart,
        updatedContent
      ) as NotePartWithItem<T>;
      resolve(updatedContainedNotePart);
    };
    handleRefreshStructuredTable({
      structuredTable,
      structuredTableAdapter,
      entryOrTemplateId,
      computedTable,
      onUpdateTable: updateTableWithColumnUpdatesAndCells(
        // This index is ignored as we thread things back based upon the cid and the callback above
        0,
        table,
        getIdFromCidFn,
        onUpdateTableCallback,
        (_) => new Set()
      ),
    }).catch((error) => {
      reject(error);
    });
  });
}

/**
 * Entities-only refresh for a contained note part. Like `refreshContainedNotePart`, resolves when
 * the refresh callback supplies updated table content, but takes `computedTable` directly instead
 * of a full `evaluatedTableMap`, and uses `handleRefreshEntitiesInStructuredTable` so formula cells
 * and user edits are preserved.
 */
export function refreshEntitiesInContainedNotePart<T extends AnyStructuredTable>({
  containedNotePart,
  computedTable,
  getIdFromCidFn,
  entryOrTemplateId,
  getItemWithUpdatedContent,
}: {
  containedNotePart: NotePartWithItem<T>;
  computedTable: Array<Array<EvaluatedCell>>;
  getIdFromCidFn: (id: string) => string | number | undefined;
  entryOrTemplateId: string;
  getItemWithUpdatedContent: (oldContainedNotePart: NotePart, content: object) => NotePart;
}): Promise<NotePartWithItem<T>> {
  // eslint-disable-next-line no-async-promise-executor -- mirrors refreshContainedNotePart
  return new Promise((resolve, reject) => {
    assert(
      hasNLO(containedNotePart) && isStructuredTable(containedNotePart.item),
      'Trying to refresh an unexpected contained note part',
      {itemType: containedNotePart.type}
    );
    const structuredTable = containedNotePart.item;
    const structuredTableAdapter: UnknownStructuredTableAdapter =
      getStructuredTableAdapters()[structuredTable.kind];
    const table = structuredTableAdapter.toTable(structuredTable);
    const onUpdateTableCallback = (
      index: number,
      updatedContent: {
        table: TableData;
        lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
      },
      selectors?: Array<Selector>
    ): void => {
      resolve(getItemWithUpdatedContent(containedNotePart, updatedContent) as NotePartWithItem<T>);
    };
    handleRefreshEntitiesInStructuredTable({
      structuredTable,
      structuredTableAdapter,
      entryOrTemplateId,
      computedTable,
      onUpdateTable: updateTableWithColumnUpdatesAndCells(
        0,
        table,
        getIdFromCidFn,
        onUpdateTableCallback,
        (_) => new Set()
      ),
    }).catch((error) => {
      reject(error);
    });
  });
}

/**
 * Refreshes each containedNotePart in the provided NoteLinkedGroup, calling the
 * `NoteLinkedGroupAdapter.reduceGroup` after each refresh before then refreshing the next contained note
 * part.
 *
 * This function assumes that the `reduceGroup` call does not change the length or order of the contained
 * note parts.
 *
 * Each note part is refreshed in order, so if the `reduceGroup` call ends up changing a previous
 * `containedNotePart`, that previous `containedNotePart` will not be refreshed again.
 *
 * Agent keep in sync: resync_note_linked_group in
 * benchling/notebook/groups/action_functions/resync_group_action_function.py, the server-side equivalent
 * invoked instead when ELN_RESYNC_USE_BACKEND_ACTION_FUNCTION is on.
 *
 * @param params
 */
async function refreshNoteLinkedGroup(
  params: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras>
): Promise<void> {
  const {
    entryOrTemplateId,
    noteLinkedGroupAdapter,
    getItemWithUpdatedContent,
    noteLinkedGroup,
    updateGroup,
    evaluatedTableMap,
    getIdFromCidFn,
    actionFunctionContext,
  } = params;

  if (BenchlingConfig.ELN_RESYNC_USE_BACKEND_ACTION_FUNCTION) {
    await performServerSideWriteAction<GroupActionFunctionDAO.ResyncGroupResponse>({
      entryId: entryOrTemplateId,
      loadingScreenMessage: 'Re-syncing...',
      autoSaverAction: ACTION_REFRESH,
      doPerformAction: async () => {
        const responseData = await GroupActionFunctionDAO.resyncNoteLinkedGroup(
          entryOrTemplateId,
          noteLinkedGroup.provenanceUuid,
          evaluatedTableMap,
          actionFunctionContext
        );
        return {resultType: ActionResultType.SUCCESS, responseData};
      },
    });
    return;
  }

  const refreshTables = async (): Promise<void> => {
    let refreshedGroup = noteLinkedGroup;
    for (let i = 0; i < noteLinkedGroup.containedNoteParts.length; i++) {
      const containedNotePart = noteLinkedGroup.containedNoteParts[i];
      // TODO(BNCH-154562): Replace this platform-level guard with a stoichiometry-specific
      // resync override in StoichiometryNoteLinkedGroupAdapter that explicitly refreshes only
      // the structured table part (the reaction scheme diagram rebuilds reactively).
      if (isStructuredTableNotePart(containedNotePart)) {
        const updatedContainedNotePart = await refreshContainedNotePart({
          containedNotePart: containedNotePart as NotePartWithItem<AnyStructuredTable>,
          evaluatedTableMap,
          getIdFromCidFn,
          entryOrTemplateId,
          getItemWithUpdatedContent,
        });
        refreshedGroup = await noteLinkedGroupAdapter.reduceGroup(noteLinkedGroup, updatedContainedNotePart);
      }
    }
    updateGroup(refreshedGroup);
  };

  await performClientSideWriteAction({
    entryOrTemplateId,
    loadingScreenMessage: 'Refreshing...',
    autoSaverAction: ACTION_REFRESH,
    doPerformAction: refreshTables,
    analyticsEvent: ELN_ANALYTICS_ACTIONS.REFRESHED_NOTE_LINKED_GROUP,
    analyticsMetadata: {
      noteLinkedGroupKind: noteLinkedGroup.kind,
    },
  });
}

const refreshNoteLinkedGroupActionFunction = makeSimpleGroupActionFunction<unknown, unknown>({
  label: RESYNC_TABLE_LABEL,
  icon: 'arrows-rotate',
  iconAltText: 'Refresh',
  onInvoke: refreshNoteLinkedGroup,
  getDisabled: ({locked}) => locked,
});

export default refreshNoteLinkedGroupActionFunction;
